<template>
    <form-group name="teamMember" label="Team Member" class="team-member-dropdown-group">
        <md-input v-model="selectedMemberId" type="hidden" />
        <vue-select
            :reduce="(item) => item.memberOrCarrierId"
            label="fullName"
            :options="filteredTeamMemberOptions"
            v-model="selectedMemberId"
            :searchable="$root.isDesktop"
            :selectable="(option) => option.selectable"
            :dropdown-overlay="false"
        >
            <template v-slot:option="option">
                <div class="member-options" v-if="displayAvailabilityIndicator">
                    <div v-if="option.fullName === 'Unassigned'" class="status-indicator empty"></div>
                    <availability-indicator v-else :is-available="option.isAvailableBasedOnRosterConfiguration" />
                    <span v-if="option.selectable">
                        {{ option.fullName }}
                    </span>
                    <hr v-else />
                </div>
                <div v-else>
                    <span v-if="option.selectable">
                        {{ option.fullName }}
                    </span>
                    <hr v-else />
                </div>
            </template>
            <template v-slot:selected-option="option">
                <div class="member-options" v-if="displayAvailabilityIndicator">
                    <div v-if="option.fullName === 'Unassigned'" class="status-indicator empty"></div>
                    <availability-indicator v-else :is-available="option.isAvailableBasedOnRosterConfiguration" />
                    <span v-if="option.selectable">
                        {{ option.fullName }}
                    </span>
                </div>
                <div v-else>
                    <span v-if="option.selectable">
                        {{ option.fullName }}
                    </span>
                </div>
            </template>
        </vue-select>
    </form-group>
</template>
<script>
import { computed, getCurrentInstance, ref, onMounted, watch } from '@vue/composition-api';
import { AvailabilityIndicator } from '@/components';
import { FeatureManager } from '@/directives';
import { FEATURE_NAMES } from '@/utils/constants';

export default {
    name: 'TeamMemberOptions',
    mixins: [FeatureManager],
    props: {
        selected: {
            default: null,
            type: String
        },
        teamRegionIdFilter: {
            type: Number,
            default: null
        },
        strictTeamRegionFilter: {
            type: Boolean,
            default: true
        },
        displayAvailabilityIndicator: {
            type: Boolean,
            default: false
        },
        availableTeamMemberIds: {
            type: Array,
            default: () => []
        }
    },

    data() {
        return {
            FEATURE_NAMES
        };
    },
    components: {
        AvailabilityIndicator
    },
    setup(props, context) {
        const selectedMemberId = ref(null);

        const vm = getCurrentInstance();
        const teamMembers = ref([]);

        onMounted(async () => {
            teamMembers.value = await vm.proxy.$root.$store.dispatch('team/FETCH_TEAM_MEMBERS');
            selectedMemberId.value = props.selected;
        });

        watch(
            () => selectedMemberId.value,
            (newVal) => {
                const model = teamMembers.value.find((member) => member.publicUserId === newVal);
                context.emit('selectedOption', newVal, model);
            }
        );

        watch(
            () => props.availableTeamMemberIds,
            (newVal) => {
                if (!props.displayAvailabilityIndicator) 
                    return;

                teamMembers.value = teamMembers.value.map((member) => ({
                    ...member,
                    isAvailableBasedOnRosterConfiguration: false
                }));
                newVal.forEach((publicUserId) => {
                    const teamMember = teamMembers.value.find((x) => x.publicUserId === publicUserId);
                    if (teamMember) {
                        teamMember.isAvailableBasedOnRosterConfiguration = true;
                    }
                });
            }
        );
        const filteredTeamMemberOptions = computed(() => {
            const members = teamMembers.value
                .filter((member) => {
                    const teamRegionIdProp = member.teamRegionId || null;
                    if (props.teamRegionIdFilter === 0 || props.teamRegionIdFilter === null) 
                        return true;
                    if (teamRegionIdProp === null) 
                        return true;
                    return teamRegionIdProp === props.teamRegionIdFilter;
                })
                .map((member) => {
                    const publicUserIdProp = member.publicUserId;
                    const fullNameProp = member.fullName || `${member.firstName || ''} ${member.lastName || ''}`.trim();
                    const code = member.memberCode || null;
                    return {
                        memberOrCarrierId: publicUserIdProp,
                        fullName: code ? `${fullNameProp} (${code})` : fullNameProp,
                        memberCode: code,
                        selectable: true,
                        teamRegionId: member.teamRegionId || null,
                        isAvailableBasedOnRosterConfiguration: member.isAvailableBasedOnRosterConfiguration || false
                    };
                });
            return members;
        });

        watch(filteredTeamMemberOptions, (newValue) => {
            if (newValue.length === 0) {
                selectedMemberId.value = null;
            }
            const selectedMember = newValue.find((member) => member.memberOrCarrierId === selectedMemberId.value);
            if (!selectedMember) {
                selectedMemberId.value = null;
            }
        });

        return {
            selectedMemberId,
            filteredTeamMemberOptions
        };
    }
};
</script>
<style scoped>
.member-options {
    display: flex;
    align-items: center;
}
.member-code {
    font-size: 11px;
    color: #666;
    margin-left: 2px;
}
.empty {
    width: 0;
}
</style>
